import { useState } from 'react';
import { client as api } from '../services/api';
import { Order } from '../types/types';
import { useSchedule } from '../context/ScheduleContext';

const getCpfOrCnpjType = (value: string) => {
  const cleanedValue = value.replace(/\D/g, '');
  if (cleanedValue.length === 11) return 'cpf';
  if (cleanedValue.length === 14) return 'cnpj';
  return null;
};

export const useFetchOrder = () => {
  const [order, setOrder] = useState<Order | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const [statuses, setStatuses] = useState<Record<string, boolean>>({});
  const { setData } = useSchedule();

  const fetchOrder = async (searchValue: string) => {
    setLoading(true);
    setError(undefined);

    try {
      const urlParams = new URLSearchParams(window.location.search);
      const orderId = urlParams.get('order_id');

      if (orderId) {
        setData(prevState => ({
          ...prevState,
          pedidoEcommerce: orderId
        }));
        const response = await api.get('/agendamento/buscarpedidogar/', { params: { pedidoEcommerce: orderId } });
        setOrder(response.data);
        await checkScheduledOrder([response.data]);
      } else {
        if (!searchValue) {
          setError('Por favor, forneça um valor válido');
          return;
        }

        const cpfOrCnpjType = getCpfOrCnpjType(searchValue);

        if (cpfOrCnpjType) {
          const response = await api.get('/agendamento/buscarpedidogar/', { params: { [cpfOrCnpjType]: searchValue } });
          if (response.status === 200 && response.data.codigo === 265) {
            setError('Erro ao buscar pedidos. Tente novamente.');
            setLoading(false);
            return;
          }
          setOrder(response.data);
          await checkScheduledOrder([response.data]);
        } else {
          const response = await api.get('/agendamento/buscaitem/', { params: { idGar: searchValue } });
          if (response.data.codigo === 801) {
            setError(response.data.mensagem);
            return;
          }
          if (response.status === 200 && response.data.codigo === 265) {
            setError('Erro ao buscar pedidos. Tente novamente.');
            setLoading(false);
            return;
          }
          const ecommerceOrder = response.data.pedidoEcommerce;

          if (ecommerceOrder) {
            const searchGarId = await api.get('/agendamento/buscarpedidogar/', { params: { pedidoEcommerce: ecommerceOrder } });
            setData(prevState => ({
              ...prevState,
              pedidoEcommerce: ecommerceOrder
            }));
            setOrder(searchGarId.data);
            await checkScheduledOrder([searchGarId.data]);
          }
        }
      }
    } catch (error) {
      setError('Erro ao buscar pedidos. Tente novamente.');
      setLoading(false);
    } finally {
      setLoading(false);
    }
  };

  const checkScheduledOrder = async (orders: Order[]) => {
    const statusMap: Record<string, boolean> = {};
    const flatOrders = orders.flat();

    try {
      await Promise.all(
        flatOrders.map(async (order) => {
          const response = await api.get('/agendamento/status/', { params: { idGar: order.id } });
          statusMap[order.id] = response.data.status?.descricao === "Agendado";
        })
      );
      setStatuses(statusMap);
    } catch (error) {
      console.error('Erro ao buscar status dos pedidos:', error);
    }
  };

  return { order, loading, error, fetchOrder, statuses, setStatuses };
};
